import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { fetchVideoMeta } from '@kit/shared/lib/video-oembed';
import { Result, ServiceError } from '@kit/shared/result';
import type { Database } from '@kit/supabase/database';

import { removeDeferredPublicCopies } from './dancer-media-library';
import {
  publishVideoSourceToR2,
  signPrivateVideoSource,
} from './dancer-media-video-source';
import type { DancerMediaContext } from './dancer-media.context';
import {
  isValidExternalVideoUrl,
  normalizeExternalVideoUrl,
} from './dancer-media.mappers';
import type {
  DancerMedia,
  MediaType,
  VideoLinkInput,
} from './dancer-media.schema';

/**
 * Native-video → Mux ingest (BAL-721, decision D1 / R1).
 *
 * Copies the just-finalized raw video out of the private DANCER_MEDIA bucket
 * into R2 (the vendor-agnostic ingest origin) and creates a Mux asset from the
 * R2 URL, requesting free Whisper captions (Rule 11). The Mux `passthrough` is
 * the `artist_media.id`, so the `video.asset.ready` webhook reconciles the row
 * (writing `mux_playback_id`) without a lookup table.
 *
 * Robustness: this NEVER throws out of finalize. Any failure (storage read, R2
 * write, Mux create, or the persist) is recorded as `mux_status='errored'` so
 * it surfaces on the reconciliation index (R5) instead of silently leaving a
 * broken tile. `media` is mutated in place so the finalize response carries the
 * ingest state.
 *
 * The Mux SDK is never touched here — all vendor calls go through the injected
 * {@link VideoService} (Rule 11 / R7); R2 access goes through {@link R2Service}.
 */
export async function ingestNativeVideoToMux(
  ctx: DancerMediaContext,
  media: Database['public']['Tables']['artist_media']['Row'],
  userId: string,
  logContext: Record<string, unknown>,
): Promise<void> {
  // Only native uploads (storage-backed, no external host) are ingested.
  if (!media.storage_path || media.external_url) return;

  const flagErrored = async (
    reason: string,
    detail?: unknown,
    extra?: Record<string, unknown>,
  ): Promise<void> => {
    ctx.log('error', `Mux ingest failed: ${reason}`, {
      ...logContext,
      mediaId: media.id,
      detail: detail instanceof Error ? detail.message : detail,
    });
    try {
      await ctx.client
        .from('artist_media')
        .update({ mux_status: 'errored', ...extra })
        .eq('id', media.id);
    } catch {
      // Best-effort — the reconciliation index / retry sweep resweeps.
    }
    media.mux_status = 'errored';
    if (extra?.r2_key && typeof extra.r2_key === 'string') {
      media.r2_key = extra.r2_key;
    }
    // The dancer may have made the video private while it was preparing;
    // the hide then left its R2 source for the Mux webhook. When no asset
    // was created no webhook comes, so remove the public copies here. A
    // shown row is left alone. Never throws: finalize must not fail here.
    try {
      const cleanup = await removeDeferredPublicCopies(ctx, media.id);
      if (cleanup.isFailure) throw cleanup.getError();
    } catch (error) {
      ctx.log('warn', 'Could not remove the public copies of a hidden video', {
        ...logContext,
        mediaId: media.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  try {
    const video = ctx.getVideoService();
    const sourceResult =
      media.shown_on_profile === false
        ? await signPrivateVideoSource(ctx.client, media)
        : await publishVideoSourceToR2(
            ctx.client,
            ctx.getR2Service(),
            media,
            userId,
          );
    if (sourceResult.isFailure) {
      const failure = sourceResult.getError();
      await flagErrored(failure.message, failure.originalError);
      return;
    }
    const source = sourceResult.getValue();
    const r2Key = source.r2Key;

    // Hand the source URL to Mux. passthrough = artist_media.id lets the
    //    webhook reconcile the row; Whisper captions are free (Rule 11).
    const asset = await video.createAssetFromUrl({
      url: source.url,
      passthrough: media.id,
      generateSubtitles: true,
    });
    if (asset.isFailure) {
      // Record the R2 key we already wrote so a retry doesn't re-copy.
      await flagErrored(
        'mux asset create failed',
        asset.getError(),
        r2Key ? { r2_key: r2Key } : undefined,
      );
      return;
    }

    // Persist the ingest state. The playback id arrives later on the webhook.
    const created = asset.getValue();
    const { error } = await ctx.client
      .from('artist_media')
      .update({
        provider: 'mux',
        r2_key: r2Key,
        mux_asset_id: created.assetId,
        mux_status: 'preparing',
      })
      .eq('id', media.id);
    if (error) {
      await flagErrored(
        'persist ingest state failed',
        error,
        r2Key ? { r2_key: r2Key } : undefined,
      );
      return;
    }

    media.provider = 'mux';
    media.r2_key = r2Key;
    media.mux_asset_id = created.assetId;
    media.mux_status = 'preparing';
    ctx.log('info', 'Mux ingest started', {
      ...logContext,
      mediaId: media.id,
      muxAssetId: created.assetId,
    });
  } catch (error) {
    await flagErrored('unexpected ingest error', error);
  }
}

/**
 * Add video link (YouTube, Vimeo, etc.)
 */
export async function addVideoLink(
  ctx: DancerMediaContext,
  userId: string,
  input: VideoLinkInput,
): Promise<Result<DancerMedia>> {
  try {
    // Validate URL format. Pasted links often carry surrounding spaces or
    // an http:// scheme; both are normalized before the check.
    const url = normalizeExternalVideoUrl(input.url);
    if (!isValidExternalVideoUrl(url)) {
      return Result.fail(
        ServiceError.validation('Invalid video URL. Must be YouTube or Vimeo.'),
      );
    }

    // Check video count limit (max 5)
    const { count, error: countError } = await ctx.client
      .from('artist_media')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('media_type', 'video');

    if (countError) {
      return Result.fail(ServiceError.database(countError.message, countError));
    }

    if (count && count >= 5) {
      return Result.fail(
        ServiceError.validation('Maximum 5 videos allowed per profile.'),
      );
    }

    // Resolve a display title (+ cached thumbnail) from the provider's
    // oEmbed API so the clip never renders as "Untitled clip". Prefer a
    // user-supplied title; fall back to the oEmbed title. Best-effort: a
    // slow/failed fetch (3s timeout) just leaves nulls. `file_name` is
    // the canonical display field for both uploads and links (the public
    // profile + categorisation grid read it); keep `title` in sync for
    // the editorial metadata editor.
    const meta = await fetchVideoMeta(url);
    const resolvedTitle =
      input.title?.trim() || meta.title?.slice(0, 200) || null;

    // The public profile loads media by professional_profile_id, not
    // user_id. Without this link the clip is saved, listed in the dancer's
    // own settings, and absent from the public profile (88 rows hidden this
    // way, 2026-05 to 2026-10). Same lookup as finalize_dancer_media_upload.
    const { data: professionalProfile, error: profileError } = await ctx.client
      .from('professional_profiles')
      .select('id')
      .eq('user_id', userId)
      .eq('profile_type', 'dancer')
      .limit(1)
      .maybeSingle();

    if (profileError) {
      return Result.fail(
        ServiceError.database(profileError.message, profileError),
      );
    }

    // Create video record
    const insertPayload = {
      user_id: userId,
      professional_profile_id: professionalProfile?.id ?? null,
      media_type: 'video' as MediaType,
      external_url: url,
      file_name: resolvedTitle,
      title: resolvedTitle,
      description: input.description || null,
      thumbnail_url: meta.thumbnailUrl,
    };

    const { data: media, error: dbError } = await ctx.client
      .from('artist_media')
      .insert(insertPayload)
      .select()
      .single();

    if (dbError) {
      return Result.fail(ServiceError.database(dbError.message, dbError));
    }

    return Result.ok(media);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'addVideoLink',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}
